import type { CSSProperties, ReactNode } from 'react';
import { healthBand, stageMeta, type StageKey } from '@/lib/pipelineAdapter';
import type { FieldSource } from '@/lib/types';

interface ChipProps {
  children: ReactNode;
  color?: string;
  bg?: string;
  dot?: string;
  style?: CSSProperties;
  size?: 'md' | 'sm';
  icon?: ReactNode;
}

export function Chip({
  children,
  color = '#64748b',
  bg = '#f1f5f9',
  dot,
  style,
  size = 'md',
  icon,
}: ChipProps) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: size === 'sm' ? '2px 7px' : '3px 9px',
        borderRadius: 999,
        background: bg,
        color,
        fontSize: size === 'sm' ? 11 : 12,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        lineHeight: 1.4,
        ...style,
      }}
    >
      {dot && (
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: 999,
            background: dot,
          }}
        />
      )}
      {icon}
      {children}
    </span>
  );
}

interface AvatarProps {
  name?: string;
  color: string;
  initials?: string;
  size?: number;
  ring?: string;
}

export function Avatar({ name, color, initials, size = 24, ring }: AvatarProps) {
  return (
    <span
      title={name}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        borderRadius: 999,
        background: color,
        color: 'white',
        fontSize: size <= 24 ? 10 : 12,
        fontWeight: 600,
        flexShrink: 0,
        boxShadow: ring ? `0 0 0 2px ${ring}` : 'none',
        letterSpacing: 0.2,
      }}
    >
      {initials ||
        (name || '?')
          .split(' ')
          .map((s) => s[0])
          .slice(0, 2)
          .join('')}
    </span>
  );
}

interface LogoProps {
  color: string;
  label: string;
  size?: number;
  radius?: number;
}

export function Logo({ color, label, size = 32, radius = 8 }: LogoProps) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        borderRadius: radius,
        background: color,
        color: 'white',
        fontSize: size <= 24 ? 10 : 13,
        fontWeight: 700,
        flexShrink: 0,
        letterSpacing: 0.3,
      }}
    >
      {label}
    </span>
  );
}

interface ScoreBarProps {
  value: number;
  max?: number;
  color?: string;
  showLabel?: boolean;
}

export function ScoreBar({ value, max = 5, color = '#3730a3', showLabel = true }: ScoreBarProps) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ display: 'inline-flex', gap: 2 }}>
        {Array.from({ length: max }).map((_, i) => (
          <span
            key={i}
            style={{
              width: 14,
              height: 4,
              borderRadius: 2,
              background: i < value ? color : '#e2e8f0',
            }}
          />
        ))}
      </span>
      {showLabel && (
        <span
          style={{
            fontSize: 11,
            color: '#475569',
            fontVariantNumeric: 'tabular-nums',
            fontWeight: 500,
          }}
        >
          {value}/{max}
        </span>
      )}
    </span>
  );
}

interface HealthRingProps {
  score: number;
  size?: number;
  stroke?: number;
}

export function HealthRing({ score, size = 32, stroke = 3 }: HealthRingProps) {
  const band = healthBand(score);
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (score / 100) * c;
  return (
    <span style={{ position: 'relative', display: 'inline-block', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="#e2e8f0"
          strokeWidth={stroke}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={band.color}
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <span
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: size <= 28 ? 9 : 10,
          fontWeight: 700,
          color: band.color,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {score}
      </span>
    </span>
  );
}

interface StageChipProps {
  stage: StageKey;
  size?: 'md' | 'sm';
}

export function StageChip({ stage, size = 'md' }: StageChipProps) {
  const m = stageMeta[stage];
  return (
    <Chip dot={m.dot} bg={m.bg} color={m.color} size={size}>
      {m.label}
    </Chip>
  );
}

export function StageChipInline({ stage }: { stage: StageKey }) {
  const m = stageMeta[stage];
  return (
    <span style={{ color: m.color, fontWeight: 500 }}>{m.label}</span>
  );
}

interface ViewTabsProps<T extends string> {
  value: T;
  onChange: (v: T) => void;
  views: { id: T; label: string }[];
  count: Record<string, number>;
}

export function ViewTabs<T extends string>({ value, onChange, views, count }: ViewTabsProps<T>) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 4,
        alignItems: 'center',
        borderBottom: '1px solid #e8ebf0',
      }}
    >
      {views.map((v) => (
        <button
          key={v.id}
          onClick={() => onChange(v.id)}
          style={{
            padding: '10px 14px',
            fontSize: 13,
            fontWeight: 500,
            color: value === v.id ? '#0f172a' : '#64748b',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            position: 'relative',
            borderBottom:
              value === v.id ? '2px solid #3730a3' : '2px solid transparent',
            marginBottom: -1,
          }}
        >
          {v.label}{' '}
          {count[v.id] != null && (
            <span
              style={{
                marginLeft: 6,
                fontSize: 11,
                color: '#94a3b8',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {count[v.id]}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

interface SegRowProps<T extends string> {
  value: T;
  onChange: (v: T) => void;
  opts: [T, string][];
  compact?: boolean;
}

export function SegRow<T extends string>({ value, onChange, opts, compact }: SegRowProps<T>) {
  return (
    <div
      style={{
        display: 'inline-flex',
        padding: 2,
        background: '#f1f5f9',
        borderRadius: 6,
        gap: 2,
        marginTop: compact ? 0 : 6,
      }}
    >
      {opts.map(([k, lbl]) => (
        <button
          key={k}
          onClick={() => onChange(k)}
          style={{
            padding: compact ? '4px 10px' : '4px 10px',
            fontSize: compact ? 12 : 11,
            fontWeight: 500,
            border: 'none',
            background: value === k ? 'white' : 'transparent',
            color: value === k ? '#0f172a' : '#64748b',
            borderRadius: 4,
            cursor: 'pointer',
            boxShadow: value === k ? '0 1px 2px rgba(15,23,42,0.06)' : 'none',
          }}
        >
          {lbl}
        </button>
      ))}
    </div>
  );
}

export function Stat({
  label,
  value,
  accent = '#0f172a',
}: {
  label: string;
  value: string | number;
  accent?: string;
}) {
  return (
    <div style={{ textAlign: 'right' }}>
      <div
        style={{
          fontSize: 10,
          color: '#64748b',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: accent,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
    </div>
  );
}

export function Section({
  title,
  children,
}: {
  title: ReactNode;
  children: ReactNode;
}) {
  return (
    <div style={{ marginTop: 20 }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.6,
          color: '#94a3b8',
          textTransform: 'uppercase',
          marginBottom: 10,
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

export function Field({
  label,
  children,
  source,
}: {
  label: string;
  children: ReactNode;
  /** When provided, renders a small color-coded chip next to the label indicating where the value came from (call/email/both). */
  source?: FieldSource;
}) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          fontSize: 12,
          color: '#475569',
          marginBottom: 6,
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span>{label}</span>
        {source && <SourceChip kind={source} />}
      </div>
      {children}
    </div>
  );
}

const SOURCE_META: Record<
  Exclude<FieldSource, 'none'>,
  { label: string; bg: string; fg: string; tooltip: string }
> = {
  call: {
    label: 'call',
    bg: '#dbeafe',
    fg: '#1d4ed8',
    tooltip: 'Derived from Gong call transcripts',
  },
  email: {
    label: 'email',
    bg: '#dcfce7',
    fg: '#166534',
    tooltip: 'Derived from Salesforce emails',
  },
  both: {
    label: 'both',
    bg: '#ede9fe',
    fg: '#6d28d9',
    tooltip: 'Corroborated in both calls and emails',
  },
};

/**
 * Tiny pill that tells the user where a deal-table value was derived from.
 * Hidden when `kind === 'none'` so Unknown/empty fields stay clean.
 */
export function SourceChip({ kind }: { kind: FieldSource }) {
  if (kind === 'none') return null;
  const meta = SOURCE_META[kind];
  return (
    <span
      title={meta.tooltip}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '1px 6px',
        borderRadius: 999,
        background: meta.bg,
        color: meta.fg,
        fontSize: 10,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
      }}
    >
      {meta.label}
    </span>
  );
}
